import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Radio } from "lucide-react";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, signup } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await signup(email, password, name || email.split("@")[0], displayName || name || email.split("@")[0]);
      toast.success("Welcome!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 bg-[#0A0D14] overflow-hidden">
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1728659964407-ed9a942ec2de?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D14]/60 via-[#0A0D14]/85 to-[#0A0D14]" />
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(16,185,129,0.08) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      <Card className="relative z-10 w-full max-w-md bg-[#121824] border-white/10 p-8">
        <div className="flex items-center gap-3 mb-8">
          <img src="/bettrackr-logo.svg" alt="BetTrackr" className="h-10 w-10" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">NFL Betting Tracker</div>
            <div className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Bet<span className="text-emerald-400">Trackr</span>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <h1 className="text-3xl font-black uppercase tracking-tight text-white mb-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {mode === "login" ? "Welcome Back" : "Get In The Game"}
          </h1>
          <p className="text-sm text-slate-400">Track every leg. Live. Always on.</p>
        </div>

        <Button
          data-testid="google-login-button"
          type="button"
          onClick={googleLogin}
          className="w-full bg-white text-slate-900 hover:bg-slate-100 mb-4"
        >
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] uppercase tracking-widest text-slate-500">or email</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <>
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-400">Name (private)</Label>
                <Input id="name" data-testid="signup-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0E1420] border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label htmlFor="display-name" className="text-xs uppercase tracking-widest text-slate-400">Display name (shown publicly)</Label>
                <Input id="display-name" data-testid="signup-display-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Dave" className="bg-[#0E1420] border-white/10 text-white mt-1" maxLength={30} />
                <p className="text-[10px] text-slate-500 mt-1">Used in Discord alerts. Never your email.</p>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-400">Email</Label>
            <Input id="email" type="email" required data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0E1420] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-400">Password</Label>
            <Input id="password" type="password" required data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0E1420] border-white/10 text-white mt-1" />
          </div>
          <Button
            data-testid={mode === "login" ? "login-submit-button" : "signup-submit-button"}
            type="submit"
            disabled={busy}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider"
          >
            {busy ? "..." : (mode === "login" ? "Log In" : "Create Account")}
          </Button>
        </form>

        <button
          data-testid="toggle-auth-mode"
          type="button"
          onClick={() => setMode(mode === "login" ? "signup" : "login")}
          className="mt-4 w-full text-center text-xs text-slate-400 hover:text-white uppercase tracking-widest"
        >
          {mode === "login" ? "Need an account? Sign up" : "Already have an account? Log in"}
        </button>
      </Card>
    </div>
  );
}
